<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import {
  ClickAndCollectFormSchema,
  ClickAndCollectFormWithFullNameCheckSchema,
} from '../../utils/API/Cart/cartAPISchema';
import { FEEDBACK_TEXT, isFullNameTooLong } from '../../utils/validationHelpers';
import { Log } from '../../utils/API/Logging/LoggingAPI';
import { scrollIntoViewThenFocus } from '../../utils/scrollHelpers';
import { delayForOrderPlacementTesting } from '../../utils/checkoutHelpers';
import { CheckoutStages } from '../../services/models/checkout.models';
import FormInput from '../form/formInput.vue';
import { useCheckoutStateStore } from '../checkout/stores/checkoutStateStore';
import { useCustomerInformationStore } from '../checkout/stores/customerInformationStore';
import { useDeliveryInformationStore } from '../checkout/stores/deliveryInformationStore';
import { useClickAndCollectReservationStore } from './stores/clickAndCollectReservationStore';
import { useClickAndCollectLocationStore } from './stores/clickAndCollectLocationStore';
import { useShoppingCartStore } from './stores/shoppingCartStore';
import { vAutoAnimate } from '@formkit/auto-animate/vue';

const reservationStore = useClickAndCollectReservationStore();
const locationStore = useClickAndCollectLocationStore();
const shoppingCartStore = useShoppingCartStore();
const deliveryInformationStore = useDeliveryInformationStore();
const customerInformationStore = useCustomerInformationStore();
const checkoutStateStore = useCheckoutStateStore();

const emit = defineEmits<{
  confirm: [];
}>();

// Arriving at this panel is the shopping-cart-stage equivalent of reaching the
// full checkout's customer-information step - autofill from the customer's
// profile the same way, guarded by the store's own owner-email comparison
// (only overwrites the draft when the fetched account differs from whoever
// the persisted draft is currently tagged to).
onMounted(async () => {
  await customerInformationStore.setFieldsIfLoggedIn();
});

const errors = ref<Record<string, string[]>>({});
const isSuccess = ref(false);
const errorSummaryVisible = ref(false);
const errorSummaryElement = ref<HTMLElement | null>(null);

const form = computed(() => ({
  firstName: customerInformationStore.fields.firstName,
  lastName: customerInformationStore.fields.lastName,
  email: customerInformationStore.fields.email,
  phoneNumber: customerInformationStore.fields.phoneNumber,
}));

const errorsExist = computed(() => Object.values(errors.value).some((e) => e.length > 0));

function updateField(field: keyof typeof form.value, value: string) {
  customerInformationStore.setField(field, value);
}

const isNameTooLong = () =>
  isFullNameTooLong(form.value.firstName || '', form.value.lastName || '');

function validateField(field: string) {
  if ((field === 'firstName' || field === 'lastName') && isNameTooLong()) {
    errors.value = { ...errors.value, [field]: [FEEDBACK_TEXT().NAME_MAXLENGTH_EXCEEDED] };
    return;
  }

  const fieldValue = form.value[field as keyof typeof form.value];
  const fieldSchema = ClickAndCollectFormSchema.shape?.[field as keyof typeof ClickAndCollectFormSchema.shape];

  if (!fieldSchema) return;

  const result = fieldSchema.safeParse(fieldValue);

  if (result.success) {
    errors.value = { ...errors.value, [field]: [] };
  } else {
    const errorMessages = result.error.issues.map((issue) => issue.message);
    errors.value = { ...errors.value, [field]: errorMessages };
  }
}

/**
 * Validates and (if valid) confirms the click & collect form.
 *
 * `stockSufficient` (defaults to `true`, unchanged behavior for existing
 * callers) lets a caller run this validation unconditionally alongside a
 * separate stock check, while still deferring scroll/focus AND the final
 * order-confirmed state to the caller when stock is insufficient — the
 * product-list stock summary wins scroll/focus priority in that case, and no
 * order should be confirmed even if this form is itself valid.
 */
async function handleSubmit(options?: { stockSufficient?: boolean }): Promise<boolean> {
  const stockSufficient = options?.stockSufficient ?? true;
  errors.value = {};
  errorSummaryVisible.value = false;

  const result = ClickAndCollectFormWithFullNameCheckSchema.safeParse(form.value);

  if (!result.success) {
    result.error.issues.forEach((issue) => {
      const field = String(issue.path?.[0] ?? '');
      if (field) {
        if (!errors.value[field]) {
          errors.value[field] = [];
        }
        errors.value[field].push(issue.message);
      }
    });
    isSuccess.value = false;
    errorSummaryVisible.value = true;
    if (stockSufficient) {
      await nextTick();
      if (errorSummaryElement.value) {
        await scrollIntoViewThenFocus(errorSummaryElement.value, { offset: 150 });
      }
    }
    return false;
  }

  if (!stockSufficient) {
    isSuccess.value = false;
    return true;
  }

  const reserved = await reservationStore.reserveOrder();

  if (!reserved) {
    isSuccess.value = false;
    return false;
  }

  const cartId = shoppingCartStore.cart?.id;

  // Swaps to the full-view "placing order" loading stage before clearing the
  // cart - clearing it synchronously tears down the whole cart view (this
  // panel included), and `window.location.href` below doesn't navigate away
  // instantly, so without this the customer would briefly see an emptied cart.
  checkoutStateStore.beginOrderPlacement();

  Log({
    LogLevel: 'Information',
    Message: `[ClickAndCollectPanel] Reservation confirmed with orderNumber: "${reservationStore.orderNumber}"`,
  });

  isSuccess.value = true;
  emit('confirm');

  const receiptPageUrl = checkoutStateStore.receiptPageUrl.replace(/\/$/, '');
  const cartIdParam = cartId ? `&cartId=${encodeURIComponent(cartId)}` : '';

  await delayForOrderPlacementTesting();

  window.location.href = `${receiptPageUrl}/ClickAndCollect?orderNumber=${reservationStore.orderNumber}${cartIdParam}`;

  return true;
}

defineExpose({
  handleSubmit,
});
</script>

<template>
  <div class="click-collect-panel">
    <!-- Store info -->
    <div class="click-collect-panel__store-section">
      <span class="click-collect-panel__store-label">Valgt butikk for henting</span>
      <div class="click-collect-panel__store">
        <span class="click-collect-panel__store-marker"></span>
        <div class="click-collect-panel__store-info">
          <strong class="click-collect-panel__store-name">{{ locationStore.storeName }}</strong>
          <span class="click-collect-panel__store-address">{{ locationStore.address }}</span>
        </div>
      </div>
    </div>

    <!-- Contact form -->
    <form class="click-collect-panel__form" @submit.prevent="handleSubmit">
      <div v-if="errorSummaryVisible && errorsExist" ref="errorSummaryElement" tabindex="-1"
        class="checkout-form__error-summary">
        <h3 v-if="errorsExist">
          {{ $t('errorsInForm') }}
        </h3>
        <ul role="alert" class="checkout-form__error-list" :aria-hidden="errorsExist" v-auto-animate>
          <template v-for="(messages, path) in errors">
            <li v-if="messages[0]" :key="path" class="checkout-form__error">
              {{ $t(path) }}: {{ messages[0] }}
            </li>
          </template>
        </ul>
      </div>

      <FormInput :model-value="form.firstName" input-class="checkout-form__input" label="Fornavn"
        :error="errors.firstName?.[0]" label-class="checkout-form__label" type="text" :required="true"
        autocomplete="given-name" @update:model-value="updateField('firstName', $event)"
        @blur="validateField('firstName')" />

      <FormInput :model-value="form.lastName" input-class="checkout-form__input" label="Etternavn"
        :error="errors.lastName?.[0]" label-class="checkout-form__label" type="text" :required="true"
        autocomplete="family-name" @update:model-value="updateField('lastName', $event)"
        @blur="validateField('lastName')" />

      <FormInput :model-value="form.email" input-class="checkout-form__input" label="E-post" :error="errors.email?.[0]"
        label-class="checkout-form__label" type="email" :required="true" autocomplete="email"
        @update:model-value="updateField('email', $event)" @blur="validateField('email')" />

      <FormInput :model-value="form.phoneNumber" input-class="checkout-form__input" label="Telefon"
        :error="errors.phoneNumber?.[0]" label-class="checkout-form__label" type="tel" :required="true"
        autocomplete="tel-national" @update:model-value="updateField('phoneNumber', $event)"
        @blur="validateField('phoneNumber')" />

      <p class="click-collect-panel__sms-hint">
        {{ $t('clickAndCollectSmsNotification') }}
      </p>
    </form>

    <p v-if="isSuccess" class="click-collect-panel__success" role="status">
      Ordre bekreftet
    </p>
  </div>
</template>
